import React, {MutableRefObject, useEffect, useRef} from 'react'
import AdyenCheckout from '@adyen/adyen-web'
import {getConfig} from '../../services/config'
import {getLogger} from '../../services/logging'
import PaymentMethodsResponse from '@adyen/adyen-web/dist/types/core/ProcessResponse/PaymentMethodsResponse'

interface AdyenPaymentFormProps {
  paymentMethods: PaymentMethodsResponse
  paymentSession: Record<string, any>
  isSavedPayment: boolean
  onPaymentCompleted?: (result: any) => void
}

const logger = getLogger('AdyenPaymentForm')

// ----------------------------------------------------------------------------
//  Adyen Payment Form Rendering
// ----------------------------------------------------------------------------

const renderAdyenCheckout = async (
  paymentContainer: MutableRefObject<any>,
  paymentMethods: PaymentMethodsResponse,
  paymentSession: Record<string, any>,
  isSavedPayment: boolean,
  onPaymentCompleted: (result: any) => void
): Promise<Function> => {
  const payMessage = isSavedPayment ? 'Save Payment' : 'Make Payment'
  const {adyenClientId, adyenEnvironment} = getConfig()
  const paymentMethodConfigs = {
    card: {
      hasHolderName: true,
      holderNameRequired: true,
      billingAddressRequired: true,
      name: 'Credit or Debit Card'
    }
  }

  const configuration = {
    paymentMethodsResponse: paymentMethods,
    paymentMethodsConfiguration: paymentMethodConfigs,
    clientKey: adyenClientId,
    environment: adyenEnvironment,
    locale: 'en-US',
    session: {
      id: paymentSession?.id,
      sessionData: paymentSession?.sessionData
    },
    translations: {
      'en-US': {
        confirmPurchase: payMessage,
        payButton: payMessage
      }
    },
    onPaymentCompleted: (result: any) => {
      if (onPaymentCompleted) {
        onPaymentCompleted(result)
      }
    },
    onError: (error: any) => {
      logger.logError('Adyen payment error', {error})
    }
  }

  const checkout = await AdyenCheckout(configuration)
  const component = checkout
    .create('dropin', {showPayButton: true})
    .mount(paymentContainer.current)

  // return component cleanup function
  return () => {
    try {
      if (checkout && component) {
        checkout.remove(component)
      }
    } catch (e) {
      logger.logWarn('Failed to cleanup adyen payment component', {error: e})
    }
  }
}

// ----------------------------------------------------------------------------
//  Adyen Payment Form Component
// ----------------------------------------------------------------------------

const AdyenPaymentForm = ({
  paymentMethods,
  paymentSession,
  isSavedPayment,
  onPaymentCompleted
}: AdyenPaymentFormProps) => {
  // get reference to container element
  const paymentContainer = useRef(null)

  useEffect(() => {
    if (paymentContainer && paymentSession) {
      const noop = () => {}

      // render the adyen payment component
      const checkoutPromise = renderAdyenCheckout(
        paymentContainer,
        paymentMethods,
        paymentSession,
        isSavedPayment,
        onPaymentCompleted || noop
      )

      // handle errors
      checkoutPromise.catch(e => {
        logger.logError('Error rendering Adyen payment form', {error: e})
      })

      // cleanup function - removes payment component
      return () => {
        // invoke the cleanup function
        checkoutPromise.then(cleanup => cleanup())
      }
    } else {
      return
    }
  }, [paymentContainer, paymentSession])

  return (
    <div className="mb-8">
      <div ref={paymentContainer} />
    </div>
  )
}

export default AdyenPaymentForm
